import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import StoreSelector from '../components/StoreSelector'
import { ShoppingList, Product, ShoppingListItem, getProductPrice, getBestPrice, getProductShelfNumber } from '../types'
import { shoppingListsApi, productsApi } from '../services/api'
import toast from 'react-hot-toast'

const ShoppingListDetailPage = () => {
  const { listName } = useParams<{ listName: string }>()
  const navigate = useNavigate()
  const [list, setList] = useState<ShoppingList | null>(null)
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingProduct, setIsAddingProduct] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [quantityUnit, setQuantityUnit] = useState<'pcs' | 'kg' | 'g' | 'l' | 'ml'>('pcs')
  const [selectedStore, setSelectedStore] = useState<string>('all')

  useEffect(() => {
    if (listName) {
      loadData()
    }
  }, [listName])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [listsResponse, productsResponse] = await Promise.all([
        shoppingListsApi.getLists(),
        productsApi.getProducts()
      ])

      // Load the specific list
      if (listsResponse.success && listsResponse.data) {
        const lists = Array.isArray(listsResponse.data) ? listsResponse.data : []
        const foundList = lists.find(l => l.name === decodeURIComponent(listName!))
        if (foundList) {
          setList(foundList)
        } else {
          toast.error('Ostoslistaa ei löytynyt')
          navigate('/shopping-lists')
          return
        }
      }

      // Load all products for adding
      if (productsResponse.success && productsResponse.data) {
        const products = Array.isArray(productsResponse.data) ? productsResponse.data : []
        setAllProducts(products)
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Tietojen lataus epäonnistui')
      navigate('/shopping-lists')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddProduct = async () => {
    if (!selectedProduct || !list) return

    // Get the price for the selected store
    const price = selectedStore === 'all' 
      ? getBestPrice(selectedProduct).price 
      : getProductPrice(selectedProduct, selectedStore)

    const storeInfo = selectedStore === 'all' 
      ? getBestPrice(selectedProduct).store 
      : selectedStore

    try {
      const response = await shoppingListsApi.addToList({
        listName: list.name,
        productName: selectedProduct.name,
        quantity,
        quantityUnit,
        unitPrice: price,
        store: storeInfo
      })

      if (response.success) {
        toast.success(`${selectedProduct.name} lisätty listalle`)
        loadData() // Reload to get updated list
        setSelectedProduct(null)
        setQuantity(1)
        setQuantityUnit('pcs')
        setSearchTerm('')
        setIsAddingProduct(false)
      } else {
        toast.error(response.error || 'Tuotteen lisääminen epäonnistui')
      }
    } catch (error) {
      console.error('Add product error:', error)
      toast.error('Verkkovirhe')
    }
  }

  const handleRemoveProduct = async (productName: string) => {
    if (!list || !confirm(`Poista ${productName} listalta?`)) return

    try {
      const response = await shoppingListsApi.removeFromList(list.name, productName)
      if (response.success) {
        toast.success('Tuote poistettu listalta')
        loadData() // Reload to get updated list
      } else {
        toast.error(response.error || 'Tuotteen poisto epäonnistui')
      }
    } catch (error) {
      console.error('Remove product error:', error)
      toast.error('Verkkovirhe')
    }
  }

  const calculateTotal = () => {
    if (!list) return 0
    return list.items.reduce((total, item) => {
      const currentPrice = getCurrentItemPrice(item)
      return total + ((item.quantity || 0) * currentPrice)
    }, 0)
  }

  const getCurrentItemPrice = (item: ShoppingListItem) => {
    // Find the product to get current pricing
    const product = allProducts.find(p => p.name === item.name)
    if (!product) return item.unitPrice || 0

    if (selectedStore === 'all') {
      return getBestPrice(product).price
    } else {
      return getProductPrice(product, selectedStore)
    }
  }

  const getItemPriceInfo = (item: ShoppingListItem) => {
    const product = allProducts.find(p => p.name === item.name)
    if (!product) {
      return {
        price: item.unitPrice || 0,
        store: item.store || 'Unknown',
        isBestPrice: false,
        isDifferentStore: false
      }
    }

    if (selectedStore === 'all') {
      const best = getBestPrice(product)
      return {
        price: best.price,
        store: best.store,
        isBestPrice: true,
        isDifferentStore: false
      }
    } else {
      const storePrice = product.prices.find(p => p.store === selectedStore)
      if (storePrice) {
        const best = getBestPrice(product)
        return {
          price: storePrice.price,
          store: selectedStore,
          isBestPrice: storePrice.price === best.price,
          isDifferentStore: false
        }
      } else {
        // Show best price with indication it's from different store
        const best = getBestPrice(product)
        return {
          price: best.price,
          store: best.store,
          isBestPrice: false,
          isDifferentStore: true
        }
      }
    }
  }

  const filteredProducts = allProducts.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !list?.items.some(item => item.name === product.name) // Don't show products already in list
  )

  const getDisplayPrice = (product: Product) => {
    if (selectedStore === 'all') {
      const best = getBestPrice(product)
      return {
        price: best.price,
        store: best.store,
        isBestPrice: true
      }
    } else {
      const storePrice = product.prices.find(p => p.store === selectedStore)
      if (storePrice) {
        return {
          price: storePrice.price,
          store: selectedStore,
          isBestPrice: false
        }
      } else {
        // Show best price with indication it's from different store
        const best = getBestPrice(product)
        return {
          price: best.price,
          store: best.store,
          isBestPrice: false,
          isDifferentStore: true
        }
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!list) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Listaa ei löytynyt</h3>
        <Link to="/shopping-lists" className="mt-4 text-primary-600 hover:text-primary-700">
          Takaisin listoihin
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{list.name}</h1>
            <p className="text-sm text-gray-600">
              {list.items.length} tuotetta • {calculateTotal().toFixed(2)} €
            </p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/shopping/${encodeURIComponent(list.name)}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Kauppaan
            </Link>
            <Link
              to="/shopping-lists"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Takaisin
            </Link>
          </div>
        </div>
        
        {/* Store Selector */}
        <div className="pt-4 border-t border-gray-200">
          <StoreSelector 
            selectedStore={selectedStore}
            onStoreChange={setSelectedStore}
          />
          {selectedStore !== 'all' && (
            <p className="mt-2 text-sm text-gray-600">
              Hinnat näytetään kaupasta {selectedStore}. Jos tuote ei ole saatavilla tässä kaupassa, näytetään paras hinta muista kaupoista.
            </p>
          )}
        </div>
      </div>

      {/* Add Product Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Lisää tuotteita</h2>
          <button
            onClick={() => setIsAddingProduct(!isAddingProduct)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Lisää tuote
          </button>
        </div>

        {isAddingProduct && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hae tuotetta
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Kirjoita tuotteen nimi..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                
                {searchTerm && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white">
                    {filteredProducts.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">Ei tuloksia</div>
                    ) : (
                      filteredProducts.map(product => {
                        const displayPrice = getDisplayPrice(product)
                        return (
                          <div
                            key={product.name}
                            onClick={() => setSelectedProduct(product)}
                            className={`p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                              selectedProduct?.name === product.name ? 'bg-primary-50' : ''
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium text-gray-900">{product.name}</div>
                                <div className="text-xs text-gray-500">
                                  <span className={displayPrice.isDifferentStore ? 'text-orange-600' : ''}>
                                    {displayPrice.store}
                                  </span>
                                  {displayPrice.isDifferentStore && ' (eri kauppa)'}
                                  {' • '}{product.category}
                                  {product.prices.length > 1 && ` • ${product.prices.length} kaupassa`}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className={`text-sm font-medium ${displayPrice.isBestPrice ? 'text-green-600' : 'text-gray-900'}`}>
                                  {displayPrice.price.toFixed(2)} €
                                  {displayPrice.isBestPrice && <span className="text-xs ml-1">(paras)</span>}
                                  {displayPrice.isDifferentStore && <span className="text-xs ml-1 text-orange-600">(eri kauppa)</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {selectedProduct && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Määrä
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={quantity}
                          onChange={(e) => setQuantity(parseFloat(e.target.value) || 1)}
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <select
                          value={quantityUnit}
                          onChange={(e) => setQuantityUnit(e.target.value as 'pcs' | 'kg' | 'g' | 'l' | 'ml')}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="pcs">kpl</option>
                          <option value="kg">kg</option>
                          <option value="g">g</option>
                          <option value="l">l</option>
                          <option value="ml">ml</option>
                        </select>
                      </div>
                    </div>
                    <button
                      onClick={handleAddProduct}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Lisää listalle
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current List Items */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Listan sisältö</h2>
          
          {list.items.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Tyhjä lista</h3>
              <p className="mt-1 text-sm text-gray-500">Aloita lisäämällä tuotteita.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tuote
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Määrä
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Yksikköhinta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hyllynro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Yhteensä
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Toiminnot</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {list.items.map((item, index) => {
                    const priceInfo = getItemPriceInfo(item)
                    const currentPrice = getCurrentItemPrice(item)
                    const product = allProducts.find(p => p.name === item.name)
                    const shelfNumber = product ? getProductShelfNumber(product, selectedStore) : ''
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">
                            <span className={priceInfo.isDifferentStore ? 'text-orange-600' : ''}>
                              {priceInfo.store}
                            </span>
                            {priceInfo.isDifferentStore && ' (eri kauppa)'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)} {item.quantityUnit || 'kpl'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className={`font-medium ${priceInfo.isBestPrice ? 'text-green-600' : 'text-gray-900'}`}>
                            {priceInfo.price.toFixed(2)} €
                            {priceInfo.isBestPrice && <span className="text-xs ml-1">(paras)</span>}
                            {priceInfo.isDifferentStore && <span className="text-xs ml-1 text-orange-600">(eri kauppa)</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {shelfNumber || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {((item.quantity || 0) * currentPrice).toFixed(2)} €
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRemoveProduct(item.name)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Poista
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-sm font-medium text-gray-900">
                      Yhteensä:
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-primary-600">
                      {calculateTotal().toFixed(2)} €
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ShoppingListDetailPage 